import {
  LayoutGrid,
  CheckCircle2,
  MessageCircle,
  LightbulbIcon,
  RefreshCw,
} from "lucide-react";
import Markdown from "react-markdown";
import CardImage from "../tool_components/CardImage";
import PdfDownloadButton from "../pdf/PdfDownloadButton";
import { Button, Text } from "@core/index";
import useImagePdf from "../pdf/useImagePdf";
import { ClassroomManagementResponseDto } from "@packages/api/madlenAPI.schemas";
import ClassroomManagementCardPDF from "./ClassroomManagementCardPdf";
import { tC } from "../../utils/TranslateWithContentLanguage";
import { useState } from "react";
import { useAppSelector } from "@packages/store/hooks";
import PdfCustomizationModal from "@features/common/components/pdf-customization-modal";
import EditableMarkdown from "../tool_components/EditableMarkdown";
import { useQueryClient } from "@tanstack/react-query";
import {
  getGetClassroomManagementQueryKey,
  getGetUserClassroomManagementsQueryKey,
  updateClassroomManagement,
} from "@packages/api/madlenAPI";

export interface ClassroomManagementCardProps {
  tools: ClassroomManagementResponseDto;
  isImageLoading?: boolean;
  onCreateAgain: () => void;
  isEditable?: boolean;
}

export const ClassroomManagementCard = ({
  tools,
  isImageLoading = false,
  onCreateAgain,
  isEditable = false,
}: ClassroomManagementCardProps) => {
  const finalImageUrl = useImagePdf(tools.imageUrl);
  const [showPdfCustomizationModal, setShowPdfCustomizationModal] =
    useState(false);
  const customizationSettings = useAppSelector(
    (state) => state.user.teacher?.preferences?.pdfOptions,
  );
  const queryClient = useQueryClient();
  const contentLanguage = tools.contentLanguage || "tr";

  return (
    <div>
      <div className="mb-7">
        <CardImage
          courseName={tools?.courseName ?? ""}
          gradeLevel={Number(tools?.gradeLevel) || 0}
          imageUrl={tools?.imageUrl}
          videoUrl={tools?.videoUrl}
          createdAt={tools?.createdAt}
          isLoading={isImageLoading}
          theme="fuchsia"
          topic={
            <div className="flex items-center gap-4 justify-center">
              <div className="p-3 rounded-xl bg-fuchsia-100 dark:bg-fuchsia-900/50 text-fuchsia-600 dark:text-fuchsia-400">
                <LayoutGrid size={24} />
              </div>
              <div>
                <Text variant="h1" className="mb-2">
                  {tools?.title}
                </Text>
              </div>
            </div>
          }
          contentLanguage={contentLanguage}
        />
        <div className="flex justify-end gap-2 mt-2">
          {onCreateAgain && (
            <Button
              onClick={onCreateAgain}
              variant="solid"
              size="md"
              textColor="text-white dark:text-white"
              bgColor="bg-gradient-to-r from-fuchsia-500 to-purple-600 hover:from-fuchsia-600 hover:to-purple-700"
              rounded="xl"
            >
              <RefreshCw size={18} />
              <span>{tC(contentLanguage, "common.createAnother")}</span>
            </Button>
          )}
          <PdfDownloadButton
            openCustomizationModal={() => setShowPdfCustomizationModal(true)}
            pdfTitle={tools.title || ""}
            document={
              <ClassroomManagementCardPDF
                tool={{
                  ...tools,
                  imageUrl: finalImageUrl,
                }}
                customizationSettings={customizationSettings}
              />
            }
            btnTheme="bg-gradient-to-r from-fuchsia-500 to-purple-600"
          />
        </div>
      </div>
      <div className="grid grid-cols-1 gap-6">
        {tools?.strategies?.map((strategy, index) => (
          <div
            key={index}
            className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200/50 dark:border-gray-700/50 overflow-hidden"
          >
            <div className="p-6">
              {/* Strategy Title and Description */}
              <div className="flex items-center gap-4 mb-6">
                <div className="p-3 rounded-xl bg-fuchsia-100 dark:bg-fuchsia-900/50 text-fuchsia-600 dark:text-fuchsia-400">
                  <LayoutGrid size={24} />
                </div>
                <div>
                  <Text variant="h2" className="font-bold mb-2">
                    <EditableMarkdown
                      value={strategy.title}
                      isEditable={isEditable ?? true}
                      handleSave={async (newValue) => {
                        if (!tools.strategies) return;

                        const updatedStrategies = [...tools.strategies];
                        updatedStrategies[index] = {
                          ...updatedStrategies[index],
                          title: newValue,
                        };

                        await updateClassroomManagement(tools.id, {
                          strategies: updatedStrategies,
                        });

                        await queryClient.invalidateQueries({
                          queryKey: getGetUserClassroomManagementsQueryKey(),
                        });
                        await queryClient.invalidateQueries({
                          queryKey: getGetClassroomManagementQueryKey(tools.id),
                        });
                      }}
                    />
                  </Text>
                  <Text
                    variant="p"
                    className="text-gray-600 dark:text-gray-400"
                  >
                    <EditableMarkdown
                      value={strategy.description}
                      isEditable={isEditable ?? true}
                      handleSave={async (newValue) => {
                        if (!tools.strategies) return;

                        const updatedStrategies = [...tools.strategies];
                        updatedStrategies[index] = {
                          ...updatedStrategies[index],
                          description: newValue,
                        };

                        await updateClassroomManagement(tools.id, {
                          strategies: updatedStrategies,
                        });

                        await queryClient.invalidateQueries({
                          queryKey: getGetUserClassroomManagementsQueryKey(),
                        });
                        await queryClient.invalidateQueries({
                          queryKey: getGetClassroomManagementQueryKey(tools.id),
                        });
                      }}
                    />
                  </Text>
                </div>
              </div>

              {/* Implementation Steps */}
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle2
                    className="text-fuchsia-500 dark:text-fuchsia-400"
                    size={20}
                  />
                  <Text
                    variant="h3"
                    className="text-fuchsia-700 dark:text-fuchsia-300"
                  >
                    {tC(
                      contentLanguage,
                      "classroomManagement.implementationSteps",
                    )}
                  </Text>
                </div>
                <ul className="space-y-2 pl-7">
                  {strategy.implementation_steps &&
                    strategy.implementation_steps.map((step, stepIndex) => (
                      <li
                        key={stepIndex}
                        className="text-gray-700 dark:text-gray-300 list-disc"
                      >
                        <EditableMarkdown
                          value={step}
                          isEditable={isEditable ?? true}
                          handleSave={async (newValue) => {
                            if (!tools.strategies) return;

                            const updatedStrategies = [...tools.strategies];
                            const updatedSteps = [
                              ...updatedStrategies[index].implementation_steps,
                            ];
                            updatedSteps[stepIndex] = newValue;

                            updatedStrategies[index] = {
                              ...updatedStrategies[index],
                              implementation_steps: updatedSteps,
                            };

                            await updateClassroomManagement(tools.id, {
                              strategies: updatedStrategies,
                            });

                            await queryClient.invalidateQueries({
                              queryKey:
                                getGetUserClassroomManagementsQueryKey(),
                            });
                            await queryClient.invalidateQueries({
                              queryKey: getGetClassroomManagementQueryKey(
                                tools.id,
                              ),
                            });
                          }}
                        />
                      </li>
                    ))}
                </ul>
              </div>

              {/* Additional Suggestions */}
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <LightbulbIcon
                    className="text-fuchsia-500 dark:text-fuchsia-400"
                    size={20}
                  />
                  <Text
                    variant="h3"
                    className="text-fuchsia-700 dark:text-fuchsia-300"
                  >
                    {tC(
                      contentLanguage,
                      "classroomManagement.additionalSuggestions",
                    )}
                  </Text>
                </div>
                <ul className="space-y-2 pl-7">
                  {strategy.additional_suggestions &&
                    strategy.additional_suggestions.map(
                      (suggestion, sugIndex) => (
                        <li
                          key={sugIndex}
                          className="text-gray-700 dark:text-gray-300 list-disc"
                        >
                          <EditableMarkdown
                            value={suggestion}
                            isEditable={isEditable ?? true}
                            handleSave={async (newValue) => {
                              if (!tools.strategies) return;

                              const updatedStrategies = [...tools.strategies];
                              const updatedSuggestions = [
                                ...updatedStrategies[index]
                                  .additional_suggestions,
                              ];
                              updatedSuggestions[sugIndex] = newValue;

                              updatedStrategies[index] = {
                                ...updatedStrategies[index],
                                additional_suggestions: updatedSuggestions,
                              };

                              await updateClassroomManagement(tools.id, {
                                strategies: updatedStrategies,
                              });

                              await queryClient.invalidateQueries({
                                queryKey:
                                  getGetUserClassroomManagementsQueryKey(),
                              });
                              await queryClient.invalidateQueries({
                                queryKey: getGetClassroomManagementQueryKey(
                                  tools.id,
                                ),
                              });
                            }}
                          />
                        </li>
                      ),
                    )}
                </ul>
              </div>

              {/* Parent Communication Tips */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <MessageCircle
                    className="text-fuchsia-500 dark:text-fuchsia-400"
                    size={20}
                  />
                  <Text
                    variant="h3"
                    className="text-fuchsia-700 dark:text-fuchsia-300"
                  >
                    {tC(
                      contentLanguage,
                      "classroomManagement.parentCommunicationTips",
                    )}
                  </Text>
                </div>
                <ul className="space-y-2 pl-7">
                  {strategy.parent_communication_tips &&
                    strategy.parent_communication_tips.map((tip, tipIndex) => (
                      <li
                        key={tipIndex}
                        className="text-gray-700 dark:text-gray-300 list-disc"
                      >
                        <EditableMarkdown
                          value={tip}
                          isEditable={isEditable ?? true}
                          handleSave={async (newValue) => {
                            if (!tools.strategies) return;

                            const updatedStrategies = [...tools.strategies];
                            const updatedTips = [
                              ...updatedStrategies[index]
                                .parent_communication_tips,
                            ];
                            updatedTips[tipIndex] = newValue;

                            updatedStrategies[index] = {
                              ...updatedStrategies[index],
                              parent_communication_tips: updatedTips,
                            };

                            await updateClassroomManagement(tools.id, {
                              strategies: updatedStrategies,
                            });

                            await queryClient.invalidateQueries({
                              queryKey:
                                getGetUserClassroomManagementsQueryKey(),
                            });
                            await queryClient.invalidateQueries({
                              queryKey: getGetClassroomManagementQueryKey(
                                tools.id,
                              ),
                            });
                          }}
                        />
                      </li>
                    ))}
                </ul>
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* PDF Customization Modal */}
      <PdfCustomizationModal
        show={showPdfCustomizationModal}
        onClose={() => setShowPdfCustomizationModal(false)}
        hideSchoolLogoOptions
        documentComponent={
          <ClassroomManagementCardPDF
            tool={{
              ...tools,
              imageUrl: finalImageUrl,
            }}
            customizationSettings={customizationSettings}
          />
        }
      />
    </div>
  );
};

export default ClassroomManagementCard;
